import { Check, ShieldCheck, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import styles from './SessionScreen.module.css';

export type SessionStep = { id: string; label: string };

export type SessionNotice = {
  tone: 'prompt' | 'failure';
  title: string;
  description: string;
  action: ReactNode;
};

export type SessionScreenProps = {
  appName: string;
  message: string;
  steps: readonly SessionStep[];
  currentStep: string;
  progressLabel: string;
  footnote: string;
  notice?: SessionNotice | undefined;
};

const SCREEN_STATES = { prompt: 'prompt', failure: 'failed' } as const;

const screenStateOf = (notice: SessionNotice | undefined): string =>
  notice === undefined ? 'progress' : SCREEN_STATES[notice.tone];

type StepState = 'done' | 'active' | 'failed' | 'pending';

const stepStateOf = (index: number, currentIndex: number, failed: boolean): StepState => {
  if (index < currentIndex) return 'done';
  if (index > currentIndex) return 'pending';
  return failed ? 'failed' : 'active';
};

const SessionMark = ({ appName, failed }: { appName: string; failed: boolean }) => (
  <div className={styles['mark']} aria-hidden="true">
    <span className={styles['halo']} />
    <span className={styles['orbit']} />
    <span className={styles['letter']}>{failed ? <TriangleAlert size={28} /> : appName.charAt(0)}</span>
  </div>
);

const SessionSteps = ({
  steps,
  currentStep,
  failed,
}: {
  steps: readonly SessionStep[];
  currentStep: string;
  failed: boolean;
}) => {
  const currentIndex = steps.findIndex((step) => step.id === currentStep);
  return (
    <ol className={styles['steps']}>
      {steps.map((step, index) => {
        const state = stepStateOf(index, currentIndex, failed);
        return (
          <li
            key={step.id}
            className={styles['step']}
            data-state={state}
            aria-current={state === 'active' ? 'step' : undefined}
          >
            <span className={styles['dot']}>
              {state === 'done' ? <Check size={12} strokeWidth={3} aria-hidden="true" /> : null}
            </span>
            <span className={styles['stepLabel']}>{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
};

const SessionStatus = ({
  message,
  progressLabel,
  notice,
}: Pick<SessionScreenProps, 'message' | 'progressLabel' | 'notice'>) => {
  if (notice === undefined) {
    return (
      <>
        <p className={styles['message']} role="status">
          {message}
        </p>
        <span className={styles['progress']} role="progressbar" aria-label={progressLabel} />
      </>
    );
  }
  return (
    <>
      <h2 className={styles['noticeTitle']}>{notice.title}</h2>
      <p className={styles['description']}>{notice.description}</p>
      <div className={styles['action']}>{notice.action}</div>
    </>
  );
};

export const SessionScreen = ({
  appName,
  message,
  steps,
  currentStep,
  progressLabel,
  footnote,
  notice,
}: SessionScreenProps) => {
  const failed = notice?.tone === 'failure';
  return (
    <main className={styles['screen']} data-state={screenStateOf(notice)}>
      <div className={styles['backdrop']} aria-hidden="true" />
      <section className={styles['card']}>
        <SessionMark appName={appName} failed={failed} />
        <h1 className={styles['appName']}>{appName}</h1>
        <SessionStatus message={message} progressLabel={progressLabel} notice={notice} />
        <SessionSteps steps={steps} currentStep={currentStep} failed={failed} />
        <p className={styles['footnote']}>
          <ShieldCheck size={16} aria-hidden="true" />
          {footnote}
        </p>
      </section>
    </main>
  );
};
